/**
 * @fileoverview Project application detail route
 * @module routes/sys/mgmt/$segment/projects/$projectId
 */

import type { ReactElement } from 'react';
import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router';
import { AdminGuard } from '@/features/admin';
import { useProjectApplication } from '@/features/admin/hooks/useProjectApplication';
import { ProjectDetailView } from '@/features/admin/components/ProjectDetailView';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { AdminSidebar, type AdminTab } from '@/features/admin/components/AdminSidebar';

/**
 * Project detail page component.
 *
 * Displays full details of a single project application.
 * Requires admin authentication and valid segment.
 *
 * @component
 */
function ProjectDetailPage(): ReactElement {
  const { projectId } = Route.useParams();
  const { data: project, isLoading, error } = useProjectApplication(projectId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Loading project details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500">Error: {error.message}</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Project not found</div>
      </div>
    );
  }

  return <ProjectDetailView project={project} />;
}

/**
 * Route wrapper with admin guard and segment validation.
 *
 * @component
 */
function ProjectDetailRoute(): ReactElement {
  const { segment } = Route.useParams();
  const navigate = useNavigate();
  const expectedSegment = import.meta.env.VITE_ADMIN_PATH_SEGMENT;

  // Validate segment matches expected value
  if (!expectedSegment || segment !== expectedSegment) {
    return <Navigate to="/" />;
  }

  const handleTabChange = (tab: AdminTab): void => {
    navigate({
      to: '/sys/mgmt/$segment',
      params: { segment },
      search: { tab },
    });
  };

  return (
    <AdminGuard>
      <SidebarProvider>
        <AdminSidebar activeTab="projects" onTabChange={handleTabChange} />
        <SidebarInset className="bg-white">
          <div className="bg-white min-h-screen">
            <ProjectDetailPage />
          </div>
        </SidebarInset>
      </SidebarProvider>
    </AdminGuard>
  );
}

/**
 * File-based route configuration.
 *
 * Creates route: /sys/mgmt/$segment/projects/$projectId
 */
export const Route = createFileRoute('/sys/mgmt/$segment/projects/$projectId')({
  component: ProjectDetailRoute,
});
